import { ApiService, UserType } from '@shared/services/api.service';
import { handleAsync } from '@host/utils/handleAsync';
import { Logger } from '@shared/services/logger.service';

/**
 * The share-link stash every auditorium guest carries in sessionStorage (see
 * useIsAuditoriumAudience). `waiting` marks a guest who arrived before the session
 * started: they sit on a lobby-like page with no room presence and no Jitsi join, so
 * nobody sees them and they see nobody, until the session goes live.
 */
export interface AuditoriumGuestContext {
  sessionPid: string;
  roomPid: string;
  floorPid: string;
  waiting?: boolean;
}

const CONTEXT_KEY = 'auditoriumContext';

export const readAuditoriumContext = (): AuditoriumGuestContext | null => {
  try {
    return JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || 'null');
  } catch {
    return null;
  }
};

export const writeAuditoriumContext = (ctx: AuditoriumGuestContext): void => {
  sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(ctx));
};

export const isAuditoriumWaitingGuest = (): boolean => !!readAuditoriumContext()?.waiting;

/**
 * The email an emailed invite was addressed to, kept from the join link to the guest
 * form (a different route) so the form can pre-fill and lock it. Scoped to its session.
 */
const INVITED_EMAIL_KEY = 'auditoriumInvitedEmail';

export const writeInvitedEmail = (sessionPid: string, email: string): void => {
  sessionStorage.setItem(INVITED_EMAIL_KEY, JSON.stringify({ sessionPid, email }));
};

export const readInvitedEmail = (sessionPid: string | undefined): string | null => {
  if (!sessionPid) return null;
  try {
    const stored = JSON.parse(sessionStorage.getItem(INVITED_EMAIL_KEY) || 'null');
    return stored?.sessionPid === sessionPid && stored.email ? stored.email : null;
  } catch {
    return null;
  }
};

export const clearInvitedEmail = (): void => {
  sessionStorage.removeItem(INVITED_EMAIL_KEY);
};

/**
 * Put a guest into the live auditorium: presence on the auditorium room and the
 * share-link stash without the waiting flag. The caller then navigates to the guest
 * workplace, which renders the meeting. Used both straight after the device check and
 * when a waiting guest is transferred.
 */
export const placeGuestInAuditorium = async (params: {
  sessionPid: string;
  hostPid: string;
  roomPid: string;
  floorPid: string;
}): Promise<void> => {
  // Passing room_pid bypasses the "host online → not_updated" early return so the
  // GuestPresence row is actually created (otherwise the guest bootstrap 404s).
  const [presenceErr] = await handleAsync(() =>
    ApiService.request<any>({
      method: 'PATCH',
      endpoint: '/workplace/guest/presence/self',
      data: { host_pid: params.hostPid, room_pid: params.roomPid },
      userType: UserType.GuestUser,
    })
  );
  if (presenceErr) {
    Logger.error('[Auditorium] Failed to set guest presence:', presenceErr);
  }

  writeAuditoriumContext({
    sessionPid: params.sessionPid,
    roomPid: params.roomPid,
    floorPid: params.floorPid,
  });
};
